import { apiService, toHttpError } from '@core/api';
import type {
    DataProvider,
    GetManyReferenceParams,
    Identifier,
} from 'react-admin';
import {
    appendExtraQueryParams,
    collectExtraParams,
    Page,
    queryStringFromListParams,
    toListParamsWithoutExtra,
    toRecord,
} from '../utils';

export type RestProviderOptions = {
    path: string;
    listExtraParams?: string[];
    isPaginated?: boolean;
    methods?: (keyof DataProvider)[];
};

function resolvePath(basePath: string, parentId?: string | Identifier): string {
    if (parentId && basePath.includes('{parentId}')) {
        return `/${basePath.replace('{parentId}', String(parentId))}`;
    }
    return `/${basePath}`;
}

/** React-admin passes target/id separately; merge into filter for query string builders. */
function withReferenceFilter(
    params: GetManyReferenceParams
): GetManyReferenceParams {
    const filter = params.filter as Record<string, unknown> | undefined;
    const referenceFilter =
        params.target && params.id != null
            ? { [params.target]: params.id }
            : {};
    return {
        ...params,
        filter: { ...referenceFilter, ...filter },
    };
}

export function createRestProvider(
    options: RestProviderOptions
): Partial<DataProvider> {
    const {
        path: basePath,
        listExtraParams,
        isPaginated = true,
        methods,
    } = options;

    const provider: Partial<DataProvider> = {
        getList: async (resource, params) => {
            const filter = params.filter as Record<string, unknown> | undefined;
            const parentId = filter?.memberId ?? filter?.missionId;
            const path = resolvePath(
                basePath,
                parentId as Identifier | undefined
            );

            const query = appendExtraQueryParams(
                queryStringFromListParams(
                    toListParamsWithoutExtra(params, listExtraParams)
                ),
                collectExtraParams(listExtraParams, params)
            );

            try {
                if (isPaginated) {
                    const { data } = await apiService.get<Page<unknown>>(
                        `${path}${query}`
                    );
                    return {
                        data: data.content.map(toRecord) as any[],
                        total: data.totalElements,
                    };
                } else {
                    const { data } = await apiService.get<unknown[]>(
                        `${path}${query}`
                    );
                    return {
                        data: data.map(toRecord) as any[],
                        total: data.length,
                    };
                }
            } catch (e) {
                throw toHttpError(e);
            }
        },

        getOne: async (resource, params) => {
            const path = resolvePath(basePath);
            const query = appendExtraQueryParams(
                '',
                collectExtraParams(listExtraParams, params)
            );
            try {
                const { data } = await apiService.get<unknown>(
                    `${path}/${params.id}${query}`
                );
                return { data: toRecord(data) as any };
            } catch (e) {
                throw toHttpError(e);
            }
        },

        getMany: async (resource, params) => {
            const path = resolvePath(basePath);
            const query = appendExtraQueryParams(
                '',
                collectExtraParams(listExtraParams, params)
            );
            const results = await Promise.all(
                params.ids.map(async id => {
                    try {
                        const { data } = await apiService.get<unknown>(
                            `${path}/${id}${query}`
                        );
                        return toRecord(data);
                    } catch (e) {
                        console.error(`getMany.${resource}.${id}`, e);
                        return null;
                    }
                })
            );
            return {
                data: results.filter(
                    (record): record is NonNullable<typeof record> =>
                        record != null
                ) as any[],
            };
        },

        getManyReference: async (resource, params) => {
            const referenceParams = withReferenceFilter(params);
            const filter = referenceParams.filter as
                | Record<string, unknown>
                | undefined;
            const parentId = params.id ?? filter?.memberId ?? filter?.missionId;
            const path = resolvePath(basePath, parentId);

            const query = appendExtraQueryParams(
                queryStringFromListParams(
                    toListParamsWithoutExtra(referenceParams, listExtraParams)
                ),
                collectExtraParams(listExtraParams, referenceParams)
            );

            try {
                if (!isPaginated) {
                    const { data } = await apiService.get<unknown[]>(
                        `${path}${query}`
                    );
                    return {
                        data: data.map(toRecord) as any[],
                        total: data.length,
                    };
                }
                const { data } = await apiService.get<Page<unknown>>(
                    `${path}${query}`
                );
                return {
                    data: data.content.map(toRecord) as any[],
                    total: data.totalElements,
                };
            } catch (e) {
                throw toHttpError(e);
            }
        },

        create: async (resource, params) => {
            const filter = params.data as Record<string, unknown> | undefined;
            const parentId = filter?.memberId ?? filter?.missionId;
            const path = resolvePath(
                basePath,
                parentId as Identifier | undefined
            );

            try {
                const { data } = await apiService.post<unknown>(
                    path,
                    params.data
                );
                return { data: toRecord(data) as any };
            } catch (e) {
                throw toHttpError(e);
            }
        },

        update: async (resource, params) => {
            const path = resolvePath(basePath);
            try {
                const { data } = await apiService.put<unknown>(
                    `${path}/${params.id}`,
                    params.data
                );
                return { data: toRecord(data) as any };
            } catch (e) {
                throw toHttpError(e);
            }
        },

        updateMany: async (resource, params) => {
            const path = resolvePath(basePath);
            try {
                const data = await Promise.all(
                    params.ids.map(async id => {
                        await apiService.put<unknown>(
                            `${path}/${id}`,
                            params.data
                        );
                        return id;
                    })
                );
                return { data };
            } catch (e) {
                throw toHttpError(e);
            }
        },

        delete: async (resource, params) => {
            const path = resolvePath(basePath);
            try {
                await apiService.delete(`${path}/${params.id}`);
                return { data: { id: params.id } as any };
            } catch (e) {
                throw toHttpError(e);
            }
        },

        deleteMany: async (resource, params) => {
            const path = resolvePath(basePath);
            try {
                await Promise.all(
                    params.ids.map(id => apiService.delete(`${path}/${id}`))
                );
                return { data: params.ids };
            } catch (e) {
                throw toHttpError(e);
            }
        },
    };

    if (!methods) {
        return provider;
    }

    const filteredProvider: Partial<DataProvider> = {};
    for (const method of methods) {
        if (provider[method]) {
            filteredProvider[method] = provider[method] as any;
        }
    }
    return filteredProvider;
}
